import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable } from 'rxjs';
import { delay, finalize, retryWhen, take, tap } from 'rxjs/operators';
import { ErrorService } from '../error/error.service';
import { IWorkflow, IWorkflowActivity } from './models/workflow.model';
import { IMAGE_QUALITY } from './capture-strings';

@Injectable({
  providedIn: 'root'
})
export class CaptureWorkflowService {

  private logger: Logger;
  private httpErrorResponse: HttpErrorResponse;
  public workflow: IWorkflow;
  public workflowList: Array<IWorkflow>;
  public imagesArray: Array<any>;


  constructor(
    private errorService: ErrorService,
    private httpClient: HttpClient,
    private loggingService: LoggingService,
  ) {
    this.logger = this.loggingService.getLogger("[CaptureService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
   }

// ############################################################################################################################################################################
// #############################################################################################################################################################################
// ### 		GET_WORKFLOWS            #########################################################################################################################################
// #############################################################################################################################################################################
// #############################################################################################################################################################################

  public getWorkflows(workflowsUrl): Observable<any> {
    const urlForMobileWorkflows = workflowsUrl + '?page=0&pageSize=20&direction=ASC&sort=NAME&sources=PHONE'
    return new Observable((observer) => {
      this.httpClient.get<any>(urlForMobileWorkflows)
      .pipe(retryWhen(error => error.pipe(
        delay(1000),
        take(3),
        tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
        finalize(() => {
          if (this.httpErrorResponse.status === 401 || this.httpErrorResponse.status ===  403) {
            this.logger.info('getWorkflows() httpErrorResponse === ' + this.httpErrorResponse.status);
          } else {
            this.errorService.handleHttpClientResponseError(this.httpErrorResponse, 'GET', '[CaptureService] getWorkflows()');
            observer.error(this.httpErrorResponse);
            observer.complete();
          }
        })
      )))
      .subscribe((response: any) => {
        observer.next(this.createWorkflowList(response));
        observer.complete();
      });
    });
  }

  private createWorkflowList(response) {
    let workflowList:Array<IWorkflow> = [];
    if (response._embedded && response._embedded['px:workflowResources']) {
      for (let workflowResource of response._embedded['px:workflowResources']) {
        // console.log('workflowResource', workflowResource);
        workflowList.push(this.setWorkflowData(workflowResource));
      }
      // console.log('workflowList', workflowList);
    }
    return workflowList;
  }

  private setWorkflowData(workflowResource) {
    const refactoredWorkflow: IWorkflow = {
      links: {
        groups: null,
        scans: null,
        test: null,
        self: null
      },
      active: workflowResource['active'],
      created: workflowResource['created'] ? workflowResource['created'] : null,
      destinations: workflowResource['destinations'] ? workflowResource['destinations'] : [],
      fileType: this.setFileType(workflowResource),
      imageQuality: this.setImageQuality(workflowResource),
      name: workflowResource['name'] ? workflowResource['name'] : null,
      ocrConfiguration: this.setOcrConfiguration(workflowResource),
      workflowActivities: this.setWorkflowActivityList(workflowResource)
    }
    if (workflowResource._links) {this.setWorkflowLinks(refactoredWorkflow, workflowResource._links)};
    return refactoredWorkflow;
  }

  private setFileType(workflowResource): string {
    let fileType = null;
    const workflowFileType = workflowResource['_embedded']['workflowActivities'][1]['type'];
    switch (workflowFileType) {
      case 'GENERATE_DOCX':
        fileType = 'MicrosoftWord'
        break;
      case 'GENERATE_SEARCHABLE_PDF':
        fileType = 'PDF';
        break;
    }
    return fileType;
  }

  private setImageQuality(workflowResource): string {
    let imageQuality: string = null;
    switch (workflowResource['outputImageQuality']) {
      case 'MIN':
        imageQuality = IMAGE_QUALITY.MIN;
        break;
      case 'MEDIUM':
        imageQuality = IMAGE_QUALITY.MEDIUM;
        break;
      case 'MAX':
        imageQuality = IMAGE_QUALITY.MAX;
        break;
    }
    return imageQuality;
  }

  private setOcrConfiguration(workflowResource): string {
    return workflowResource['_embedded']['workflowActivities'][1]['ocrConfiguration'];
  }

  private setWorkflowLinks(workflow, links) {
    if (links['px:groups'] && links['px:groups'].href) {
      workflow.links.groups = links['px:groups'].href;
    }
    if (links['px:scans'] && links['px:scans'].href) {
      workflow.links.scans = links['px:scans'].href;
    }
    if (links['px:test'] && links['px:test'].href) {
      workflow.links.test = links['px:test'].href;
    }
    if (links['self'] && links['self'].href) {
      workflow.links.self = links['self'].href;
    }
  }

  private setWorkflowActivityList(workflowResource) {
    let workflowActivityList: Array<IWorkflowActivity> = [];
    if (workflowResource._embedded['workflowActivities']) {
      for (let workflowActivity of workflowResource._embedded['workflowActivities']) {
        workflowActivityList.push(this.setWorkflowActivity(workflowActivity))
      }
    }
    return workflowActivityList;
  }

  private setWorkflowActivity(workflowActivity) {
// console.log('workflowActivity', workflowActivity);
    const activity: IWorkflowActivity = {
      links: {
        self: workflowActivity._links['self'] && workflowActivity._links['self'].href ? workflowActivity._links['self'].href : null,
      },
      stage: workflowActivity['stage'] ? workflowActivity['stage'] : null,
      type: workflowActivity['type'] ? workflowActivity['type'] : null
    }
    return activity;
  }
}